<template>
  <div>
    <!-- Text-and-Button:start -->
    <div class="flex items-baseline justify-between mt-10 relative">
      <h2 class="intro-y text-lg font-medium -top-1">
        {{ $t(`${viewRoot}.list`) }}
      </h2>
      <button type="button" class="btn btn-primary w-40 md:w-34 h-8" @click="newQuestionWithAnswers">
        <span>{{ $t(`${viewRoot}.new`) }}</span>
      </button>
    </div>
    <!-- Text-and-Button:end -->

    <!-- Main-container:start -->
    <div class="grid grid-cols-12 gap-6 mb-12">
      <!--Header:start-->
      <div class="intro-y col-span-12 flex flex-wrap sm:flex-nowrap items-center mt-2">
        <p class="mr-auto">{{ $t(`${viewRoot}.description`) }}</p>
      </div>
      <!--Header:end-->

      <!-- Filters:start -->
      <div class="intro-y col-span-12 flex flex-wrap sm:flex-nowrap items-center mt-2">
        <div class="w-full grid sm:grid-cols-2 lg:grid-cols-6 xl:grid-cols-4 gap-6 mt-3 sm:mt-0 sm:ml-auto md:ml-0">
          <!--Campo buscador-->
          <div class="w-full sm:w-auto mt-3 sm:mt-0 sm:ml-auto md:ml-0">
            <div class="w-full relative lg:col-span-2 xl:col-span-1 text-gray-700 dark:text-gray-300">
              <input
                type="text"
                class="form-control w-full box pr-10 placeholder-theme-13"
                :placeholder="$t(`${viewRoot}.search_text`)"
                v-model="searchInput"
                @keyup="filterQuestionWithAnswers"
              />
              <SearchIcon class="w-4 h-4 absolute my-auto inset-y-0 mr-3 right-0" />
            </div>
          </div>
        </div>

        <button @click="filterClean" type="button" class="btn btn-custom-gray-1 w-24">
          {{ $t("views.clean") }}
        </button>
      </div>
      <!-- Filters:end -->

      <!-- Data-List:start -->
      <div class="intro-y col-span-12 overflow-auto">
        <table class="table table-report -mt-2 overflow-hidden">
          <thead>
            <tr>
              <th class="whitespace-nowrap">
                <div class="grid question__with__answers__grid">
                  <div class="title__table">ID</div>
                  <div class="title__table">{{ $t(`${viewRoot}.table.title1`) }}</div>
                  <div class="title__table">{{ $t(`${viewRoot}.table.title2`) }}</div>
                </div>
              </th>
              <th class="text-center whitespace-nowrap">
                {{ $t(`${viewRoot}.table.actions`) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(question, questionKey) in questionsWithAnswers" :key="questionKey">
              <tr class="intro-x">
                <td>
                  <div class="grid question__with__answers__grid cursor-pointer">
                    <!--ID question-->
                    <div>{{ question.id }}</div>

                    <!--Difficulty Level question-->
                    <div class="text-center">{{ question.difficulty_level }}</div>

                    <!--Question-->
                    <div>{{ question.langs[0]?.question ?? " - " }}</div>
                  </div>
                </td>

                <!-- Section-Actions:start -->
                <td>
                  <div class="flex justify-center items-center">
                    <a class="flex items-center mr-3" href="javascript:;" @click="editQuestionWithAnswers(question.id)">
                      <CheckSquareIcon class="w-4 h-4 mr-1" />
                      {{ $t("views.edit") }}
                    </a>
                  </div>
                </td>
                <!-- Section-Actions:end -->
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <!-- Data-List:start -->
    </div>
    <!-- Main-container:end -->

    <!-- Pagination:start -->
    <PaginationControls
      v-model:page="pagination.page"
      v-model:per_page="pagination.per_page"
      :total_pages="pagination.total_pages"
      :total="pagination.total"
      :sizes="[3, 10, 25, 35, 50]"
      @request="getQuestionWithAnswers"
    >
      <template #summary>
        {{ $t("components.pagination.viewing", { total: questionsWithAnswers.length }) }}
      </template>
    </PaginationControls>
    <!-- Pagination:end -->
  </div>
</template>

<script>
import IlongameService from "@/services/ilongame.service";
import PaginationControls from "@/components/pagination-control/Main.vue";

export default {
  name: "QuestionWithAnswersList",
  components: {
    PaginationControls,
  },
  data() {
    return {
      viewRoot: "views.ilongame.questionsWithAnswers",
      questionsWithAnswers: [],
      searchInput: "",
      debounceTimeout: null,

      pagination: {
        page: 0,
        total_pages: 0,
        per_page: 10,
        total: 0,
      },
    };
  },
  mounted() {
    this.getInitialData();
  },
  watch: {
    "$i18n.locale": function (l) {
      this.getInitialData();
    },
  },
  methods: {
    async getInitialData() {
      await this.getQuestionWithAnswers();
    },
    async getQuestionWithAnswers() {
      const params = {
        search: this.searchInput || "",
        per_page: this.pagination.per_page,
        page: this.pagination.page,
        role: "ADMIN",
      };
      const { data, pagination } = await IlongameService.getAllQuestionWithAnswers(params, localStorage.lang);

      this.questionsWithAnswers = data;
      this.paginationSetData(pagination);
    },
    filterQuestionWithAnswers() {
      clearTimeout(this.debounceTimeout);

      this.debounceTimeout = setTimeout(() => {
        this.pagination.page = 0;
        this.getQuestionWithAnswers();
      }, 1000);
    },

    newQuestionWithAnswers() {
      localStorage.removeItem("questionWithAnswers");
      this.$router.push({ path: "/question-with-answers-new" });
    },

    editQuestionWithAnswers(id) {
      this.$router.push({ path: "/question-with-answers-edit", query: { id } });
    },

    // Pagination:start
    paginationSetData({ page = 0, per_page: perPage = 0, total = 0, total_pages: totalPages = 0 } = {}) {
      this.pagination.page = page;
      this.pagination.per_page = perPage;
      this.pagination.total = total;
      this.pagination.total_pages = totalPages;
    },
    onPageChange(newPage) {
      this.pagination.page = newPage;
      this.getQuestionWithAnswers();
    },
    onPerPageChange(newPerPage) {
      this.pagination.per_page = Number(newPerPage);
      this.pagination.page = 0;
      this.getQuestionWithAnswers();
    },
    // Pagination:end
    filterClean() {
      this.searchInput = "";
      this.getQuestionWithAnswers();
    },
  },
};
</script>

<style lang="scss" scoped>
.question__with__answers__grid {
  grid-template-columns: 10vw 10vw auto;
  align-items: center;
  width: 100%;

  @media screen and (min-width: 1400px) {
    grid-template-columns: 7% 10% auto;
  }

  .title__table {
    text-wrap: wrap;
  }

  .content__table {
    text-align: center;
  }
}
</style>
